/**
 * Pagination Controls Component
 *
 * Provides navigation controls for paginated audit log data.
 * Includes page navigation, items per page selector, and page jump functionality.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { PaginationMeta } from '@/types/audit-log';

/**
 * Pagination Controls Props
 */
interface PaginationControlsProps {
  /** Pagination metadata from API response */
  pagination: PaginationMeta;
  /** True when the backend capped the total count — renders totals as "N+" */
  isTotalCapped?: boolean;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when items per page changes */
  onLimitChange: (limit: number) => void;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * Items Per Page Options
 */
const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];

/**
 * Pagination Controls Component
 */
export function PaginationControls({
  pagination,
  isTotalCapped = false,
  onPageChange,
  onLimitChange,
  isLoading = false,
}: PaginationControlsProps) {
  const { page, limit, total, totalPages } = pagination;

  // Calculate display range
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  // Handle previous page
  const handlePrevious = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  // Handle next page
  const handleNext = () => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  };

  // Handle items per page change
  const handleLimitChange = (value: string) => {
    const newLimit = parseInt(value, 10);
    onLimitChange(newLimit);
    // Reset to page 1 when changing limit
    onPageChange(1);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 py-4">
      {/* Results Summary */}
      <div className="text-sm text-muted-foreground">
        Showing{' '}
        <span className="font-medium text-foreground">
          {startItem}-{endItem}
        </span>{' '}
        of{' '}
        <span className="font-medium text-foreground">
          {total.toLocaleString()}
          {isTotalCapped && '+'}
        </span>{' '}
        results
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center gap-6">
        {/* Items Per Page Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select value={limit.toString()} onValueChange={handleLimitChange} disabled={isLoading}>
            <SelectTrigger className="w-[70px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ITEMS_PER_PAGE_OPTIONS.map(option => (
                <SelectItem key={option} value={option.toString()}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page Navigation */}
        <div className="flex items-center gap-2">
          {/* Page Info */}
          <div className="text-sm text-muted-foreground min-w-[100px] text-center">
            Page {page} of {totalPages || 1}
            {isTotalCapped && '+'}
          </div>

          {/* Previous Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={page <= 1 || isLoading}
            aria-label="Go to previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Next Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={page >= totalPages || isLoading || totalPages === 0}
            aria-label="Go to next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
